import { HashIndexRedirect, RouteElement, RouteProtectedElement } from '@components'

import { ModuleLayout } from '@billy/design-system'
import { ReactElement, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, Route, Routes } from 'react-router'

import { Scope } from '../../enums/scope'
import { withRouteLinkClickedListener } from '../../hocs/withRouteLinkClickedListener'
import { useGetQueryFromEmberRoute } from '../../hooks/useGetQueryFromEmberRoute'
import { defaultQuery } from './constants/defaultQuery'
import { SettingsContextSwitcher } from './elements/SettingsContextSwitcher'
import { SettingsNavigation } from './elements/SettingsNavigation'
import { SettingsRoute } from './enums/settingsRoute'
import { OrganizationAccessTokensSettings } from './routes/OrganizationAccessTokensSettings'
import { OrganizationAccountingSettings } from './routes/OrganizationAccountingSettings'
import { OrganizationCardPaymentsSettings } from './routes/OrganizationCardPaymentsSettings'
import { ChartOfAccounts } from './routes/OrganizationChartOfAccounts'
import { OrganizationEInvoiceSettings } from './routes/OrganizationEInvoiceSettings'
import { OrganizationFeaturesSettings } from './routes/OrganizationFeaturesSettings'
import { OrganizationInvoiceSettings } from './routes/OrganizationInvoiceSettings'
import { OrganizationSettings } from './routes/OrganizationSettings'
import { OrganizationSettingsUsers } from './routes/OrganizationSettingsUsers'
import { SubscriptionComponent } from './routes/OrganizationSubscriptionSettings'
import { UserNotificationsSettings } from './routes/UserNotificationsSettings'
import { UserOrganizationsSettings } from './routes/UserOrganizationsSettings'
import { UserPrivacySettings } from './routes/UserPrivacySettings'
import { UserProfileSettings } from './routes/UserProfileSettings'
import { VatRatesSettings } from './routes/VatRatesSettings'
import { VatReturnSettings } from './routes/VatReturnSettings'
import * as Styled from './styles'

export const Settings = withRouteLinkClickedListener((): ReactElement => {
  const { t } = useTranslation()
  const emberQuery = useGetQueryFromEmberRoute({ queryParamsValidKeys: Object.keys(defaultQuery) })
  const modulePathname = useRef(window.location.pathname).current

  return (
    <ModuleLayout
      headerBottomActions={
        <>
          <SettingsNavigation />
        </>
      }
      headerContent={
        <Styled.SettingsContextSwitcherWrapper>
          <SettingsContextSwitcher />
        </Styled.SettingsContextSwitcherWrapper>
      }
      restoreScroll={false}
      resetScroll={true}
      title={t('settings')}
    >
      <Routes>
        <Route
          path="/"
          element={
            <RouteElement isHashRouter>
              <HashIndexRedirect
                modulePathname={modulePathname}
                to={typeof emberQuery?.initialRoute === 'string' ? emberQuery.initialRoute : SettingsRoute.Organization}
              />
            </RouteElement>
          }
        />
        <Route
          path={SettingsRoute.Organization}
          element={
            <RouteElement isHashRouter>
              <OrganizationSettings />
            </RouteElement>
          }
        />
        <Route
          path={SettingsRoute.OrganizationAccounting}
          element={
            <RouteElement isHashRouter>
              <OrganizationAccountingSettings />
            </RouteElement>
          }
        />
        <Route
          path={SettingsRoute.OrganizationAccountingVatReturn}
          element={
            <RouteProtectedElement isHashRouter scopes={Scope.VatReturnRead}>
              <VatReturnSettings />
            </RouteProtectedElement>
          }
        />
        <Route
          path={SettingsRoute.OrganizationAccountingChartOfAccounts}
          element={
            <RouteProtectedElement isHashRouter scopes={Scope.ChartOfAccountsRead}>
              <ChartOfAccounts />
            </RouteProtectedElement>
          }
        />
        <Route
          path={SettingsRoute.OrganizationAccountingFecImport}
          element={
            <RouteElement isHashRouter>
              <Navigate to={SettingsRoute.OrganizationAccounting} replace />
            </RouteElement>
          }
        />
        <Route
          path={SettingsRoute.OrganizationVatRates}
          element={
            <RouteProtectedElement isHashRouter scopes={Scope.SettingVatWrite}>
              <VatRatesSettings />
            </RouteProtectedElement>
          }
        />
        <Route
          path={SettingsRoute.OrganizationInvoicing}
          element={
            <RouteProtectedElement isHashRouter scopes={Scope.InvoiceRead}>
              <OrganizationInvoiceSettings />
            </RouteProtectedElement>
          }
        />
        <Route
          path={SettingsRoute.OrganizationEInvoicing}
          element={
            <RouteProtectedElement isHashRouter scopes={Scope.EDocumentRead}>
              <OrganizationEInvoiceSettings />
            </RouteProtectedElement>
          }
        />
        <Route
          path={SettingsRoute.OrganizationCardPayments}
          element={
            <RouteProtectedElement isHashRouter scopes={Scope.InvoiceRead}>
              <OrganizationCardPaymentsSettings />
            </RouteProtectedElement>
          }
        />
        <Route
          path={SettingsRoute.OrganizationUsers}
          element={
            <RouteProtectedElement isHashRouter scopes={[[Scope.UserRead], [Scope.UserWriteAccountant]]}>
              <OrganizationSettingsUsers />
            </RouteProtectedElement>
          }
        />
        <Route
          path={SettingsRoute.OrganizationSubscription}
          element={
            <RouteProtectedElement isHashRouter scopes={Scope.OrganizationSubscriptionWrite}>
              <SubscriptionComponent />
            </RouteProtectedElement>
          }
        />
        <Route
          path={SettingsRoute.OrganizationSubscriptionPlansSelection}
          element={
            <RouteProtectedElement isHashRouter scopes={Scope.OrganizationSubscriptionWrite}>
              <SubscriptionComponent />
            </RouteProtectedElement>
          }
        />
        <Route
          path={SettingsRoute.OrganizationAccessTokens}
          element={
            <RouteProtectedElement isHashRouter scopes={Scope.TokensRead}>
              <OrganizationAccessTokensSettings />
            </RouteProtectedElement>
          }
        />
        <Route
          path={SettingsRoute.OrganizationBetas}
          element={
            <RouteElement isHashRouter>
              <OrganizationFeaturesSettings />
            </RouteElement>
          }
        />
        <Route
          path={SettingsRoute.Profile}
          element={
            <RouteElement isHashRouter>
              <UserProfileSettings />
            </RouteElement>
          }
        />
        <Route
          path={SettingsRoute.ProfileNotifications}
          element={
            <RouteElement isHashRouter>
              <UserNotificationsSettings />
            </RouteElement>
          }
        />
        <Route
          path={SettingsRoute.ProfilePrivacy}
          element={
            <RouteElement isHashRouter>
              <UserPrivacySettings />
            </RouteElement>
          }
        />
        <Route
          path={SettingsRoute.ProfileOrganizations}
          element={
            <RouteElement isHashRouter>
              <UserOrganizationsSettings />
            </RouteElement>
          }
        />
      </Routes>
    </ModuleLayout>
  )
})
